import React, { ReactNode } from "react";

import { Box } from "@UI/Components/Box/Box";
import { Flex } from "@UI/Components/Flex/Flex";
import { Modal } from "@UI/Components/Modal/Modal";
import { Button } from "@UI/Components/Button/Button";
import { useDisclosure } from "@UI/hooks/useDisclosure";
import useMediaQuery from "@UI/hooks/useMediaQuery";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";

import { useDraftSavedPill } from "../../hooks/useDraftSavedPill";
import { useFlowBack } from "../../hooks/useFlowBack";
import { useFlowExitGuard } from "../../hooks/useFlowExitGuard";
import { useFlowThemeGuard } from "../../hooks/useFlowPCategory";
import { useTaskCreationSummary } from "../../hooks/useTaskCreationSummary";
import {
  TaskCreationHeader,
  TASK_CREATION_HEADER_HEIGHT,
} from "../../Components/TaskCreationHeader/TaskCreationHeader";
import { DraftSavedPill } from "../../Components/DraftSavedPill/DraftSavedPill";
import { TaskCreationExitModal } from "../../Components/TaskCreationExitModal/TaskCreationExitModal";
import { TaskCreationSummary } from "../../Components/TaskCreationSummary/TaskCreationSummary";
import {
  TaskCreationFlowProvider,
  useTaskCreationFlow,
} from "../../Contexts/TaskCreationFlow";
import { STEP_KEYS } from "../../config/constants";
import { DiscardConfirmationProvider } from "../../Contexts/DiscardConfirmation";
import { DescriptionDirtyProvider } from "../../Contexts/DescriptionDirty";
import messages from "../../messages";

/**
 * Figma node 2523:7569. The white panel is 511px and bleeds to the viewport's
 * left edge — there is no centred page container — and the 126px gutter is the
 * right column's own left padding rather than a gap on the row, so a later
 * `gap` cannot silently double it. Same mechanism the description screen uses.
 */
const SUMMARY_PANEL_WIDTH = "511px";
const CONTENT_GUTTER = "126px";
/**
 * Below 1319px (511 + 126 + 650 + 32) the form is narrower than its cap and
 * fills the row exactly, so with no right padding it runs into the viewport
 * edge. Above that width the cap already leaves slack on the right, so this
 * only ever adds to it.
 */
const CONTENT_RIGHT_GUTTER = "32px";
/**
 * Figma's right column, gutters excluded. Capped on a wrapper inside the
 * padding rather than on the padded column itself, where `border-box` had
 * `CONTENT_GUTTER` eating into it — the form was 618px wide, not 650.
 */
const CONTENT_WIDTH = "650px";

export interface ITaskCreationShellProps {
  children: ReactNode;
}

/**
 * The chrome itself, inside the provider so it reads the same derived state
 * every other consumer does.
 */
const TaskCreationChrome = ({ children }: ITaskCreationShellProps) => {
  const { currentStepNumber, totalSteps, currentStep } = useTaskCreationFlow();
  // Derived once here and handed to both containers, so the panel and the
  // mobile modal cannot disagree about which step is in progress.
  const { category, steps } = useTaskCreationSummary();
  const draftSaved = useDraftSavedPill();
  const handleBack = useFlowBack();
  const { isDesktop } = useMediaQuery();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const hasStep = Boolean(currentStepNumber);
  // Both guards live here rather than per screen: the chrome is the one thing
  // every screen of the flow shares, the description screen included.
  const exitGuard = useFlowExitGuard(hasStep);

  // Every screen, not just the description one: a step screen under a theme
  // this flow does not handle used to render happily (RT-4321).
  useFlowThemeGuard();

  /**
   * The auth step drops the desktop panel — `AuthStepLayout` owns that side —
   * but keeps the mobile modal.
   */
  const isAuthStep = currentStep?.key === STEP_KEYS.CONTACT_DETAILS;
  const showPanel = hasStep && isDesktop && !isAuthStep;
  const showModal = hasStep && !isDesktop;
  const hasContentColumn = hasStep && !isAuthStep;

  return (
    <>
      <TaskCreationHeader
        step={currentStepNumber}
        totalSteps={totalSteps}
        onSummaryOpen={showModal ? onOpen : undefined}
        draftSavedVisible={draftSaved.visible}
        draftSavedReplayToken={draftSaved.replayToken}
      />
      {/* Only a step screen gets the centred two-column shell. The description
          screen brings its own page container, and nesting one inside another
          would double its padding. */}
      <Box
        {...(hasStep
          ? {
              background: "sand.100",
              // Minus the header, which is this box's sibling — a bare 100vh
              // here scrolls the page by the header's height on its own.
              minHeight: `calc(100vh - ${TASK_CREATION_HEADER_HEIGHT})`,
            }
          : {})}
      >
        <Flex
          direction={{ base: "column", lg: "row" }}
          alignItems="stretch"
          {...(hasStep
            ? {
                // Fills the viewport below the header so the white panel runs
                // the full height even when the step's form is short.
                // `minHeight`, not `height`: a long form still grows the row
                // and drags the panel down with it.
                minHeight: {
                  base: "auto",
                  lg: `calc(100vh - ${TASK_CREATION_HEADER_HEIGHT})`,
                },
              }
            : {})}
        >
          {showPanel && (
            <Box
              as="aside"
              flex={`0 0 ${SUMMARY_PANEL_WIDTH}`}
              background="white"
              padding="32px"
            >
              {/* The back link lives inside the panel per Figma, so it is the
                  panel's concern and the modal never gets one. */}
              <TaskCreationSummary
                onBack={handleBack}
                category={category}
                steps={steps}
                badge={<DraftSavedPill {...draftSaved} />}
              />
            </Box>
          )}
          {/* Padding only on a step screen: the description screen brings its
              own page container, and padding here would double its gutter. */}
          <Box
            flex="1"
            minWidth={0}
            {...(hasContentColumn
              ? {
                  paddingLeft: { base: "16px", lg: CONTENT_GUTTER },
                  paddingRight: { base: "16px", lg: CONTENT_RIGHT_GUTTER },
                  paddingTop: { base: "0", lg: "32px" },
                  paddingBottom: { base: "0", lg: "80px" },
                }
              : {})}
          >
            {hasContentColumn ? (
              <Box maxWidth={{ base: "none", lg: CONTENT_WIDTH }}>
                {children}
              </Box>
            ) : (
              children
            )}
          </Box>
        </Flex>
      </Box>
      {showModal && (
        <Modal isOpen={isOpen} onClose={onClose}>
          <TaskCreationSummary category={category} steps={steps}>
            <Button
              width="100%"
              variant="solid"
              colorScheme="seaGreen"
              onClick={onClose}
            >
              <FormattedMessage {...messages["taskCreation.summary.close"]} />
            </Button>
          </TaskCreationSummary>
        </Modal>
      )}
      <TaskCreationExitModal
        isOpen={exitGuard.isOpen}
        onClose={exitGuard.onClose}
        onQuit={exitGuard.onQuit}
      />
    </>
  );
};

/**
 * Everything shared by every screen of the flow (RT-4321), mounted once at the
 * base route around the routes.
 *
 * Two jobs. It mounts `TaskCreationFlowProvider`, which is where the flow's
 * progress is derived and from which any screen or summary component reads it
 * with `useTaskCreationFlow()`. And it renders the chrome that already needs
 * those numbers: the shared header, plus the summary as a modal opened from the
 * step indicator on small screens and as a panel beside the flow from `lg` up,
 * where the indicator stays plain text because the panel already shows it.
 *
 * The description screen has no step, so it gets neither and the header shows
 * the logo alone.
 *
 * `DiscardConfirmationProvider` is mounted here too, inside the flow provider
 * it depends on and around every `useFlowBack` caller (the chrome's own panel
 * link, plus `DynamicScreen`/`SubcategoryScreen` among `children`) — RT-4321.
 *
 * `DescriptionDirtyProvider` likewise: it carries whether the entry screen
 * has anything worth losing from `DescriptionScreen` (among `children`) up to
 * `useFlowExitGuard` in the chrome, which reads it to decide whether the
 * "Leaving already?" modal has anything to warn about.
 */
export const TaskCreationShell = ({ children }: ITaskCreationShellProps) => (
  <TaskCreationFlowProvider>
    <DescriptionDirtyProvider>
      <DiscardConfirmationProvider>
        <TaskCreationChrome>{children}</TaskCreationChrome>
      </DiscardConfirmationProvider>
    </DescriptionDirtyProvider>
  </TaskCreationFlowProvider>
);
